import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Gift,
  Users,
  Copy,
  Check,
  Share2,
  Trophy,
  Coins,
  Percent,
  ExternalLink,
  Facebook,
  Instagram,
  Clock,
  CheckCircle2,
  Award,
  DollarSign,
  ChevronRight
} from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function ReferralsPage() {
  const { token, user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [codeRes, historyRes, leaderboardRes, rankRes] = await Promise.all([
        axios.get(`${API}/social/referral/code`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/social/referral/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/social/referral/leaderboard`),
        axios.get(`${API}/social/referral/my-rank`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setReferralData(codeRes.data);
      setReferralHistory(historyRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setMyRank(rankRes.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/social/referral/apply?referral_code=${applyCode.trim()}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setApplyCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply code');
    } finally {
      setApplying(false);
    }
  };

  const shareToSocial = (platform) => {
    if (!referralData) return;
    
    const text = `Join me on Bodies By Akeem! Use my code ${referralData.referral_code} for ${referralData.rewards.discount_percent}% off!`;
    const url = referralData.referral_link;
    
    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="referrals-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
          REFERRAL <span className="text-primary">REWARDS</span>
        </h1>
        <p className="text-gray-400 mt-1">Invite friends and earn rewards together</p>
      </div>

      {/* Stats Cards */}
      {referralData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical text-center">
            <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{referralData.stats.total_referrals}</div>
            <div className="text-xs text-gray-400">Total Referrals</div>
          </div>
          <div className="card-tactical text-center">
            <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{referralData.stats.completed}</div>
            <div className="text-xs text-gray-400">Completed</div>
          </div>
          <div className="card-tactical text-center">
            <Coins className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{referralData.stats.total_points_earned}</div>
            <div className="text-xs text-gray-400">Points Earned</div>
          </div>
          <div className="card-tactical text-center">
            <Clock className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{referralData.stats.pending}</div>
            <div className="text-xs text-gray-400">Pending</div>
          </div>
        </div>
      )}

      {/* Ambassador Tiers */}
      {referralData && referralData.tiers && (
        <div className="card-tactical">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-white uppercase flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Ambassador Program
            </h2>
            {referralData.ambassador?.current_tier && (
              <span 
                className="px-3 py-1 rounded-full text-sm font-semibold"
                style={{ 
                  backgroundColor: `${referralData.ambassador.current_tier.color}20`,
                  color: referralData.ambassador.current_tier.color 
                }}
              >
                {referralData.ambassador.current_tier.icon} {referralData.ambassador.current_tier.name}
              </span>
            )}
          </div>
          
          {/* Progress to next tier */}
          {referralData.ambassador?.next_tier && (
            <div className="mb-6 p-4 bg-secondary/30 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">
                  Progress to {referralData.ambassador.next_tier.name}
                </span>
                <span className="text-sm text-white font-mono">
                  {referralData.stats.completed} / {referralData.ambassador.next_tier.referrals_required}
                </span>
              </div>
              <div className="h-3 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${referralData.ambassador.progress_to_next}%`,
                    backgroundColor: referralData.ambassador.next_tier.color
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {referralData.ambassador.referrals_to_next} more referral{referralData.ambassador.referrals_to_next !== 1 ? 's' : ''} to unlock ${referralData.ambassador.next_tier.cash_reward} reward!
              </p>
            </div>
          )}
          
          {/* Total cash earned */}
          {referralData.ambassador?.total_cash_earned > 0 && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-green-500" />
                <div>
                  <div className="text-2xl font-mono text-green-400">${referralData.ambassador.total_cash_earned}</div>
                  <div className="text-sm text-gray-400">Total Cash Earned</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tier Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            {referralData.tiers.map((tier, index) => {
              const isUnlocked = referralData.stats.completed >= tier.referrals_required;
              const isCurrent = referralData.ambassador?.current_tier?.id === tier.id;
              
              return (
                <div 
                  key={tier.id}
                  className={`relative p-4 rounded-sm border transition-all ${
                    isUnlocked 
                      ? 'border-opacity-100' 
                      : 'border-gray-700 opacity-60'
                  } ${isCurrent ? 'ring-2 ring-offset-2 ring-offset-card' : ''}`}
                  style={{ 
                    borderColor: isUnlocked ? tier.color : undefined,
                    boxShadow: isCurrent ? `0 0 20px ${tier.color}40` : undefined
                  }}
                >
                  {isCurrent && (
                    <div 
                      className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-bold rounded"
                      style={{ backgroundColor: tier.color, color: '#000' }}
                    >
                      CURRENT
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className="text-4xl mb-2">{tier.icon}</div>
                    <h3 
                      className="font-semibold text-lg"
                      style={{ color: isUnlocked ? tier.color : '#6b7280' }}
                    >
                      {tier.name}
                    </h3>
                    <div className="text-sm text-gray-400 mt-1">
                      {tier.referrals_required} Referrals
                    </div>
                    <div 
                      className="text-2xl font-mono mt-3"
                      style={{ color: isUnlocked ? '#22c55e' : '#6b7280' }}
                    >
                      ${tier.cash_reward}
                    </div>
                    <div className="text-xs text-gray-500">Cash Reward</div>
                    
                    {isUnlocked && (
                      <div className="mt-3 flex items-center justify-center gap-1 text-green-400 text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        Unlocked!
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Your Referral Code */}
      {referralData && (
        <div className="card-tactical border-primary">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
              <Gift className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg text-white font-semibold">Your Referral Code</h2>
              <p className="text-sm text-gray-400">Share this code with friends to earn rewards</p>
            </div>
          </div>
          
          <div className="bg-secondary/50 rounded-sm p-4 mb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <Label className="text-xs text-gray-400 uppercase">Your Code</Label>
                <div className="font-mono text-3xl text-primary font-bold tracking-wider">
                  {referralData.referral_code}
                </div>
              </div>
              <Button
                onClick={() => copyToClipboard(referralData.referral_code)}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          <div className="bg-secondary/30 rounded-sm p-3 mb-4">
            <Label className="text-xs text-gray-400 uppercase mb-1 block">Referral Link</Label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={referralData.referral_link}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyToClipboard(referralData.referral_link)}
                className="text-gray-400 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Share Buttons */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">Share:</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => shareToSocial('facebook')}
              className="border-blue-600 text-blue-500 hover:bg-blue-600 hover:text-white"
            >
              <Facebook className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => shareToSocial('twitter')}
              className="border-sky-500 text-sky-400 hover:bg-sky-500 hover:text-white"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => shareToSocial('whatsapp')}
              className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </Button>
          </div>
        </div>
      )}

      {/* Rewards Info */}
      {referralData && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-tactical">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <Coins className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Points Reward</h3>
                <p className="text-sm text-gray-400">For you and your friend</p>
              </div>
            </div>
            <div className="text-center py-4 bg-secondary/30 rounded-sm">
              <div className="font-mono text-4xl text-yellow-500">+{referralData.rewards.points_per_referral}</div>
              <div className="text-sm text-gray-400 mt-1">points each</div>
            </div>
          </div>
          
          <div className="card-tactical">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <Percent className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Subscription Discount</h3>
                <p className="text-sm text-gray-400">For new members</p>
              </div>
            </div>
            <div className="text-center py-4 bg-secondary/30 rounded-sm">
              <div className="font-mono text-4xl text-green-500">{referralData.rewards.discount_percent}%</div>
              <div className="text-sm text-gray-400 mt-1">off first month</div>
            </div>
          </div>
        </div>
      )}

      {/* Ambassador Leaderboard */}
      <div className="card-tactical">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-white uppercase flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Ambassador Leaderboard
          </h2>
          {myRank?.rank && (
            <span className="text-sm text-gray-400">
              Your Rank: <span className="text-primary font-bold">#{myRank.rank}</span>
            </span>
          )}
        </div>
        
        {/* My Rank Card */}
        {myRank && myRank.rank && (
          <div className="mb-4 p-4 bg-gradient-to-r from-primary/20 to-transparent rounded-sm border border-primary/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center font-bold text-xl text-white">
                  #{myRank.rank}
                </div>
                <div>
                  <div className="text-white font-semibold">Your Position</div>
                  <div className="text-sm text-gray-400">
                    {myRank.total_referrals} referral{myRank.total_referrals !== 1 ? 's' : ''} completed
                  </div>
                </div>
              </div>
              {myRank.ambassador_tier && (
                <div 
                  className="px-3 py-1 rounded-full text-sm font-semibold"
                  style={{ 
                    backgroundColor: `${myRank.ambassador_tier.color}20`,
                    color: myRank.ambassador_tier.color 
                  }}
                >
                  {myRank.ambassador_tier.icon} {myRank.ambassador_tier.name}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Leaderboard Table */}
        {leaderboard.length > 0 ? (
          <div className="space-y-2">
            {leaderboard.map((entry, index) => (
              <div 
                key={entry.user_id}
                className={`flex items-center justify-between p-3 rounded-sm border ${
                  entry.rank <= 3 
                    ? 'bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/30' 
                    : 'bg-secondary/30 border-border'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    entry.rank === 1 ? 'bg-yellow-500 text-black' :
                    entry.rank === 2 ? 'bg-gray-300 text-black' :
                    entry.rank === 3 ? 'bg-amber-700 text-white' :
                    'bg-secondary text-gray-400'
                  }`}>
                    {entry.rank <= 3 ? (
                      entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'
                    ) : (
                      `#${entry.rank}`
                    )}
                  </div>
                  <div>
                    <div className="text-white font-medium">{entry.name}</div>
                    <div className="text-xs text-gray-400">
                      {entry.total_referrals} referral{entry.total_referrals !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {entry.ambassador_tier ? (
                    <div 
                      className="text-sm font-semibold"
                      style={{ color: entry.ambassador_tier.color }}
                    >
                      {entry.ambassador_tier.icon} {entry.ambassador_tier.name}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">Rising Star</div>
                  )}
                  {entry.total_cash_earned > 0 && (
                    <div className="text-xs text-green-400">${entry.total_cash_earned} earned</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No ambassadors yet</p>
            <p className="text-sm">Be the first to complete a referral!</p>
          </div>
        )}
      </div>

      {/* Apply Referral Code */}
      {!user?.referred_by && (
        <div className="card-tactical">
          <h3 className="text-lg text-white font-semibold mb-4">Have a Referral Code?</h3>
          <div className="flex gap-3">
            <Input
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="bg-secondary border-transparent uppercase"
              maxLength={8}
            />
            <Button
              onClick={handleApplyCode}
              disabled={applying}
              className="bg-primary hover:bg-red-700"
            >
              {applying ? 'Applying...' : 'Apply'}
            </Button>
          </div>
        </div>
      )}

      {/* Referral History */}
      <div className="card-tactical">
        <h3 className="font-heading text-lg text-white uppercase mb-4">Referral History</h3>
        {referralHistory.length > 0 ? (
          <div className="space-y-3">
            {referralHistory.map((ref) => (
              <div 
                key={ref.id}
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    ref.status === 'rewarded' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                  }`}>
                    {ref.status === 'rewarded' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-500" />
                    )}
                  </div>
                  <div>
                    <div className="text-white font-medium">{ref.referred_name || 'New User'}</div>
                    <div className="text-xs text-gray-400">
                      {new Date(ref.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    ref.status === 'rewarded' ? 'text-green-400' : 'text-yellow-400'
                  }`}>
                    {ref.status === 'rewarded' ? `+${ref.points_awarded} pts` : 'Pending'}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{ref.status}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No referrals yet</p>
            <p className="text-sm">Share your code to start earning rewards!</p>
          </div>
        )}
      </div>

      {/* Follow Us */}
      <div className="card-tactical">
        <h3 className="text-lg text-white font-semibold mb-4">Follow Bodies By Akeem</h3>
        <div className="flex items-center gap-4">
          <a
            href="https://www.facebook.com/bodiesbyakeem"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 rounded-sm hover:bg-blue-600/30 transition-colors"
          >
            <Facebook className="w-5 h-5" />
            <span>Facebook</span>
            <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://www.instagram.com/bodiesbyakeem"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-pink-600/20 text-pink-400 rounded-sm hover:bg-pink-600/30 transition-colors"
          >
            <Instagram className="w-5 h-5" />
            <span>Instagram</span>
            <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://www.tiktok.com/@bodiesbyakeem"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-gray-600/20 text-gray-300 rounded-sm hover:bg-gray-600/30 transition-colors"
          >
            <TikTokIcon className="w-5 h-5" />
            <span>TikTok</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
